// Hook pour chargement progressif d'images
import { useState, useEffect } from 'react';
import { MediaService, type ImageOptions } from '../Services/MediaService';
import { useNetworkSpeed } from './useNetworkSpeed';

export interface UseProgressiveImageOptions extends ImageOptions {
    fallbackSrc?: string;
    loadOriginal?: boolean; // Si true, charge toujours l'original après la qualité initiale
    instanceId?: string; // ID de l'instance pour le cache
    componentId?: string; // ID du composant pour le cache
}

export interface UseProgressiveImageReturn {
    src: string | null;
    isLoading: boolean;
    currentQuality: number | null; // Pourcentage de qualité (0-95)
    error: Error | null;
}

/**
 * Hook pour charger progressivement une image en plusieurs qualités
 * 1. Si qualité spécifiée : utiliser cette qualité
 * 2. Si qualité NON spécifiée : détecter connexion et charger qualité adaptée
 * 3. Puis charger l'original en arrière-plan
 */
export function useProgressiveImage(
    source: string | undefined,
    options: UseProgressiveImageOptions = {}
): UseProgressiveImageReturn {
    const {
        quality, // Optionnel maintenant
        fallbackSrc,
        loadOriginal = true,
        instanceId,
        componentId,
        ...imageOptions
    } = options;

    // Détecter la vitesse de connexion
    const { recommendedQuality, recommendedWidth, isDetecting } = useNetworkSpeed();

    const [src, setSrc] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [currentQuality, setCurrentQuality] = useState<number | null>(null);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        if (!source) {
            setIsLoading(false);
            return;
        }

        // Attendre la détection de réseau si aucune qualité spécifiée
        if (!quality && isDetecting) {
            return;
        }

        let isMounted = true;
        setIsLoading(true);
        setError(null);

        // Si c'est un fichier local, pas de chargement progressif
        if (MediaService.isLocalFile(source)) {
            setSrc(source);
            setCurrentQuality(null);
            setIsLoading(false);
            return;
        }

        const loadImage = async () => {
            try {
                // Phase 1: Charger version optimisée (qualité + largeur adaptées à la connexion)
                const initialQuality = quality !== undefined ? quality : recommendedQuality;
                const initialWidth = imageOptions.width || recommendedWidth;
                
                const startTime = performance.now();
                console.log(`🖼️ [${startTime.toFixed(0)}] DÉBUT chargement: ${initialWidth}px @ ${initialQuality}%${quality !== undefined ? ' (qualité forcée)' : ' (auto)'}`);

                // Créer le contexte pour le cache si les IDs sont fournis
                const cacheContext = instanceId && componentId ? {
                    instanceId,
                    componentId
                } : undefined;

                const optimizedUrl = await MediaService.getImageUrl(source, {
                    quality: initialQuality,
                    width: initialWidth,
                    height: imageOptions.height,
                    context: cacheContext
                });

                const optimizedLoaded = await preloadImage(optimizedUrl);
                
                if (!isMounted) return;

                if (optimizedLoaded) {
                    const endTime = performance.now();
                    const duration = endTime - startTime;
                    setSrc(optimizedUrl);
                    setCurrentQuality(initialQuality);
                    console.log(`✅ [${endTime.toFixed(0)}] Version optimisée chargée en ${duration.toFixed(0)}ms (${initialWidth}px @ ${initialQuality}%)`);
                }

                // Marquer comme non-loading pour afficher immédiatement
                setIsLoading(false);

                // Phase 2: Charger l'original complet (95% qualité + dimensions originales) en arrière-plan
                // Cette phase est INVISIBLE pour l'utilisateur
                console.log(`🔍 Vérification chargement original: loadOriginal=${loadOriginal}, initialQuality=${initialQuality}`);
                
                if (loadOriginal && initialQuality < 95) {
                    console.log(`🔇 Préchargement silencieux de l'original (dimensions originales @ 95%)...`);
                    
                    const maxQuality = MediaService.getMaxQuality();
                    const originalUrl = await MediaService.getImageUrl(source, {
                        quality: maxQuality,
                        context: cacheContext
                        // Pas de width/height = dimensions originales de l'image
                    });

                    // Précharger en arrière-plan sans changer isLoading
                    const originalLoaded = await preloadImage(originalUrl);
                    
                    if (!isMounted) return;

                    if (originalLoaded) {
                        // Swap invisible vers l'original (pas de changement de isLoading)
                        setSrc(originalUrl);
                        setCurrentQuality(maxQuality);
                        console.log(`✅ Original chargé silencieusement (original @ ${maxQuality}%)`);
                    }
                }
            } catch (err) {
                if (!isMounted) return;
                
                console.error('Erreur chargement image:', err);
                setError(err instanceof Error ? err : new Error('Erreur de chargement'));
                
                // Utiliser le fallback si disponible
                if (fallbackSrc) {
                    setSrc(fallbackSrc);
                    setCurrentQuality(null);
                }
                
                setIsLoading(false);
            }
        };

        loadImage();

        return () => {
            isMounted = false;
        };
    }, [source, quality, recommendedQuality, recommendedWidth, isDetecting, fallbackSrc, loadOriginal, JSON.stringify(imageOptions)]);

    return { src, isLoading, currentQuality, error };
}

/**
 * Précharge une image
 */
function preloadImage(url: string): Promise<boolean> {
    return new Promise((resolve) => {
        const img = new Image();

        img.onload = () => {
            img.onload = null;
            img.onerror = null;
            resolve(true);
        };

        img.onerror = () => {
            img.onload = null;
            img.onerror = null;
            resolve(false);
        };

        img.src = url;
    });
}

